import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@altermaind/web/ui';

type CustomBreakpoint = 'xl' | 'lg' | 'md' | 'sm' | 'xs';
type BreakingPointsInfo = {
  breakpoint: CustomBreakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktopLow: boolean;
  isDesktopMid: boolean;
  isDesktopHigh: boolean;
  isDesktop: boolean;
};
type UseBreakingPoints = (initialWidth?: number) => BreakingPointsInfo;

export const useBreakingPoints: UseBreakingPoints = (
  initialWidth = window.innerWidth,
): BreakingPointsInfo => {
  const [windowWidth, setWindowWidth] = useState<number>(initialWidth);

  const defaultBreakpoints = {
    default: '0px',
    desktop: '1367px',
    desktopLarge: '1620px',
    desktopLow: '1024px',
    desktopMid: '1200px',
    mobile: '350px',
    tablet: '600px',
  };
  const { breakpoints } = useTheme() as { breakpoints: Partial<typeof defaultBreakpoints> };

  const breakPoints = {
    xs: parseInt((breakpoints?.mobile || defaultBreakpoints.mobile)?.replace('px', ''), 10),
    sm: parseInt((breakpoints?.tablet || defaultBreakpoints.tablet).replace('px', ''), 10),
    md: parseInt((breakpoints?.desktopLow || defaultBreakpoints.desktopLow).replace('px', ''), 10),
    lg: parseInt((breakpoints?.desktopMid || defaultBreakpoints.desktopMid).replace('px', ''), 10),
    xl: parseInt(
      (breakpoints?.desktopLarge || defaultBreakpoints.desktopLarge).replace('px', ''),
      10,
    ),
  };
  const getBreakPoint = (): CustomBreakpoint => {
    if (windowWidth >= breakPoints.xl) {
      return 'xl';
    }
    if (windowWidth < breakPoints.xl && windowWidth >= breakPoints.md) {
      return 'lg';
    }
    if (windowWidth < breakPoints.md && windowWidth >= breakPoints.sm) {
      return 'md';
    }
    if (windowWidth < breakPoints.sm && windowWidth >= breakPoints.xs) {
      return 'sm';
    }

    return 'xs';
  };

  const callback = (): void => setWindowWidth(window.innerWidth);

  const breakpoint = getBreakPoint();

  const isMobile = useMemo(() => breakpoint === 'xs', [breakpoint]);

  const isTablet = useMemo(() => breakpoint === 'sm', [breakpoint]);

  const isDesktopLow = useMemo(() => breakpoint === 'md', [breakpoint]);

  const isDesktopMid = useMemo(() => breakpoint === 'lg', [breakpoint]);

  const isDesktopHigh = useMemo(() => breakpoint === 'xl', [breakpoint]);

  const isDesktop = useMemo(
    () => breakpoint === 'md' || breakpoint === 'lg' || breakpoint === 'xl',
    [breakpoint],
  );

  useEffect((): (() => void) => {
    setWindowWidth(initialWidth || window.innerWidth);
    window.addEventListener('resize', callback);
    return (): void => window.removeEventListener('resize', callback);
  }, []); //eslint-disable-line

  return { breakpoint, isMobile, isTablet, isDesktopLow, isDesktopMid, isDesktopHigh, isDesktop };
};
